import { useState, useEffect, useRef, useCallback } from "react";
import { Music, Volume2, VolumeX, Play, Pause, ExternalLink, SkipForward } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Equalizer({ isPlaying }) {
  if (!isPlaying) return null;
  return (
    <div className="flex items-end h-4 gap-[2px]" data-testid="audio-equalizer">
      {[...Array(5)].map((_, i) => (
        <span key={i} className="eq-bar" />
      ))}
    </div>
  );
}

export default function AudioPlayer() {
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const fetchPlaylist = async () => {
      try {
        const res = await axios.get(`${API}/landing/welcome-playlist`);
        if (res.data && res.data.songs && res.data.songs.length > 0) {
          setPlaylist(res.data.songs);
        }
      } catch (e) {
        console.error("Error fetching playlist:", e);
      }
    };
    fetchPlaylist();
  }, []);

  // Autoplay muted when playlist loads
  useEffect(() => {
    if (playlist.length > 0 && audioRef.current) {
      audioRef.current.muted = true;
      setIsMuted(true);
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        // Autoplay blocked
      });
    }
  }, [playlist]);

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current;
    if (audio && audio.duration) {
      setCurrentTime(audio.currentTime);
      setDuration(audio.duration);
      setProgress((audio.currentTime / audio.duration) * 100);
    }
  }, []);

  const handleEnded = useCallback(() => {
    if (playlist.length > 1) {
      const nextIdx = (currentIndex + 1) % playlist.length;
      setCurrentIndex(nextIdx);
      setTimeout(() => {
        audioRef.current?.play();
      }, 100);
    } else {
      setIsPlaying(false);
      setProgress(0);
    }
  }, [currentIndex, playlist.length]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isPlaying]);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const newMuted = !isMuted;
    audio.muted = newMuted;
    setIsMuted(newMuted);
    // If unmuting and not playing, start playing
    if (!newMuted && !isPlaying) {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isMuted, isPlaying]);

  const skipNext = useCallback(() => {
    if (playlist.length <= 1) return;
    const nextIdx = (currentIndex + 1) % playlist.length;
    setCurrentIndex(nextIdx);
    setProgress(0);
    setTimeout(() => {
      if (audioRef.current) {
        audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
      }
    }, 100);
  }, [currentIndex, playlist.length]);

  const handleSeek = useCallback((e) => {
    const audio = audioRef.current;
    if (!audio || !audio.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = x / rect.width;
    audio.currentTime = pct * audio.duration;
  }, []);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const song = playlist[currentIndex];
  if (!song) return null;

  return (
    <>
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        src={song.audio_url}
        preload="auto"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        muted={isMuted}
      />

      <section
        data-testid="audio-player-section"
        className="animate-stagger-3 px-5 py-10"
      >
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3 bg-[#056A84]/10 px-4 py-1.5 rounded-full border border-[#056A84]/15">
            <Music className="w-4 h-4 text-[#056A84]" />
            <span className="text-xs uppercase tracking-[0.15em] text-[#056A84] font-heading font-semibold">
              Centro de Alabanzas
            </span>
          </div>
          <h2 className="font-heading text-2xl font-bold text-[#111827] mb-2">
            Alabanzas para tu alma
          </h2>
          <p className="text-sm text-[#4B5563] max-w-xs mx-auto">
            Escucha música y cánticos mientras te registras en la app
          </p>
        </div>

        {/* Player Card */}
        <div className="glass-card p-5 max-w-sm mx-auto">
          <div className="flex items-center gap-4 mb-4">
            {/* Album art */}
            <div className="relative flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden shadow-md">
              <img
                src={song.cover_url}
                alt={song.title}
                className={`w-full h-full object-cover transition-transform duration-700 ${isPlaying ? "scale-110" : ""}`}
                loading="lazy"
              />
              {isPlaying && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <Equalizer isPlaying={isPlaying} />
                </div>
              )}
            </div>

            {/* Song info */}
            <div className="flex-1 min-w-0">
              <h3
                data-testid="song-title"
                className="font-heading font-bold text-[#111827] text-sm truncate"
              >
                {song.title}
              </h3>
              <p className="text-xs text-[#4B5563] truncate">{song.artist}</p>
              {song.album && (
                <p className="text-[10px] text-[#6B7280] truncate mt-0.5">{song.album}</p>
              )}
            </div>

            {/* Controls */}
            <div className="flex items-center gap-1.5">
              <button
                data-testid="audio-play-btn"
                onClick={togglePlay}
                className="w-10 h-10 rounded-full bg-[#00CCFF] text-white flex items-center justify-center hover:shadow-[0_0_16px_rgba(0,204,255,0.4)] active:scale-95 transition-all"
              >
                {isPlaying ? (
                  <Pause className="w-4 h-4" fill="currentColor" />
                ) : (
                  <Play className="w-4 h-4 ml-0.5" fill="currentColor" />
                )}
              </button>
              {playlist.length > 1 && (
                <button
                  data-testid="audio-skip-btn"
                  onClick={skipNext}
                  className="w-8 h-8 rounded-full bg-gray-100 text-[#4B5563] flex items-center justify-center hover:text-[#111827] hover:bg-gray-200 active:scale-95 transition-all"
                >
                  <SkipForward className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div
            className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden cursor-pointer group"
            onClick={handleSeek}
            data-testid="audio-progress-bar"
          >
            <div
              className="h-full bg-[#00CCFF] rounded-full transition-[width] duration-200 group-hover:bg-[#056A84]"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-[10px] text-[#6B7280]">{formatTime(currentTime)}</span>
            <span className="text-[10px] text-[#6B7280]">
              {playlist.length > 1 && `${currentIndex + 1}/${playlist.length} · `}
              {formatTime(duration)}
            </span>
          </div>
        </div>

        {/* Link to alabanzas */}
        <div className="text-center mt-6">
          <a
            data-testid="alabanzas-link"
            href="https://alabanzas.tuiglesia.global"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#056A84] text-sm font-heading font-semibold hover:text-[#00CCFF] hover:underline transition-colors"
          >
            Explorar todo el Centro de Alabanzas
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </section>

      {/* Floating Sound Button */}
      <button
        data-testid="activate-sound-btn"
        onClick={toggleMute}
        className={`fixed bottom-20 right-4 z-50 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 ${
          isMuted
            ? "bg-[#00CCFF] animate-pulse-glow"
            : "bg-[#056A84]"
        }`}
        aria-label={isMuted ? "Activar sonido" : "Silenciar"}
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5 text-white" />
        ) : (
          <Volume2 className="w-5 h-5 text-white" />
        )}
      </button>
    </>
  );
}
